import { DtoState } from "../types/shared-enums";
import { Logger } from "../logger/logger";
import { BaseDto, OwningDto } from "./base-dto";
import { MessageHelper } from "../helper/message-helper";
import { BitConverter } from "../helper/bit-converter";
import { ArrayHelper } from "../helper/array-helper";
import { DialogResultVm, IDialogRequestOptions } from "../checkresultdialog/dialogresultvm";
import { NumberHelper } from "../devexpress/format/number-helper";
import { ServiceProvider } from "../core/provider/service-provider";
import { Bit } from "../helper/bit";
import { SqafSystemId } from "../types/shared-enums";
import { DirtyFlag } from "../extend/dirty-flag";
import { CheckResultData } from "../checkresultdialog/checkresultdata";
import { ErrorHandler } from "../core/error-handler";
import { AjaxParams } from "../core/provider/provider-interfaces";

export abstract class BaseModel<T extends BaseDto> {
	public isInShadowState = false;
	public shadowSourceData: T = null;
	public shadowDirtyEnterState = false;
	public id: KnockoutObservable<number> = ko.observable(0);
	public excludedKeys = ["changedKeys"];
	public dirtyFlag = DirtyFlag.create(this, this.excludedKeys);
	public state: KnockoutObservable<DtoState> = ko.observable(0);
	public rowversion: KnockoutObservable<number[]> = ko.observable([]);
	public trackChangesSubscription: KnockoutSubscription[] = [];
	public sourceData: T; // The original state of the site assignment.

	public isEmptyState = ko.pureComputed(() => this.state() === DtoState.Empty);
	public isNewState = ko.pureComputed(() => this.state() === DtoState.New);
	public isReadOnly = ko.pureComputed(() => this.state() === DtoState.ReadOnly);
	public isUndoRunning = false;

	public abstract changedProperties: (save: boolean) => T;

	public hasChanged = ko.pureComputed(() => {
		switch (this.state()) {
			case DtoState.New:
				return true;
			case DtoState.Empty:
				return false;
			default:
				return this.dirtyFlag && this.dirtyFlag.isDirty();
		}
	});

	public isUnchanged = ko.pureComputed(() => !this.hasChanged());
	public changedKeys = ko.observableArray([]);

	protected abstract initialize: (data: T) => void;

	public isOwnSysId(): boolean {
		let that: any = this; // eslint-disable-line
		if (that.sysId == null) {
			return true;
		}
		let curSysId: number | KnockoutObservable<number> = that.sysId as any;
		let result: boolean = true;
		if (!ko.isObservable(curSysId)) {
			result = curSysId === SqafSystemId.Self;
		} else {
			result = curSysId() === SqafSystemId.Self;
		}
		return result;
	}

	public trackChanges(additionalExcludedKeys: string[] = []) {
		let curKeys = Object.keys(this);
		for (let curKey of curKeys) {
			let curValue = (this as any)[curKey];
			if (this.excludedKeys.indexOf(curKey) === -1
				&& additionalExcludedKeys.indexOf(curKey) === -1
				&& ko.isObservable(curValue)
				&& !ko.isComputed(curValue)) {
				let curKo: KnockoutObservable<any> = curValue;
				this.trackChangesSubscription.push(curKo.subscribe(() => {
					this.setChangedKey(curKey);
				}));
			}
		}
	}

	public untrackChanges() {
		if (this.trackChangesSubscription != null && this.trackChangesSubscription.length > 0) {
			let changed = this.hasChanged();

			// clear subscription
			for (let curSubscription of this.trackChangesSubscription) {
				curSubscription.dispose();
			}

			this.trackChangesSubscription = [];
			this.changedKeys([]);
			if (!changed) {
				this.resetChangeState();
			}
		}
	}

	public dispose() {
		this.untrackChanges();
	}

	public saveAsync(): Promise<boolean> { throw new Error("Not implemented"); }
	public newAsync(parentId: number): Promise<T> { throw new Error("Not implemented"); }
	public copyAsync(): Promise<T> { throw new Error("Not implemented"); }
	public loadAsync(): Promise<T> { throw new Error("Not implemented"); }

	public async deleteAsync(): Promise<boolean> {
		let oldState = this.state();

		if (oldState === DtoState.New) {
			this.state(DtoState.Deleted);
			return true;
		}

		this.state(DtoState.Deleting);
		let result = await this.saveAsync();
		if (!result) {
			this.state(oldState);
		}
		return result;
	}

	public resetChangeState() {
		this.dirtyFlag.reset();
	}

	public undo() {
		this.isUndoRunning = true;

		this.initialize(this.sourceData);
		this.changedKeys([]);
		this.isUndoRunning = false;
		this.resetChangeState();
	}

	public startShadowMode() {
		if (this.isInShadowState) {
			Logger.error("Shadow mode already started");
			return;
		}
		this.shadowDirtyEnterState = this.dirtyFlag.isDirty();
		this.isInShadowState = true;
		this.shadowSourceData = $.extend(true, {}, this.sourceData);

		// save old state since if it is new, we have to hold this state
		let oldState = this.state();
		this.changedProperties(true);
		this.state(oldState);
	}

	public endShadow() {
		if (!this.isInShadowState) {
			Logger.error("Shadow mode not running");
			return;
		}
		this.isInShadowState = false;
	}

	public commitShadow() {
		this.endShadow();
		this.sourceData = this.shadowSourceData;
		this.shadowSourceData = null;
	}

	public discardShadow() {
		this.endShadow();
		let lastData = this.shadowSourceData;
		this.undo();
		this.sourceData = lastData;
		this.shadowSourceData = null;
		if (!this.shadowDirtyEnterState) {
			this.resetChangeState();
		}
	}

	public getPropertyMapper(result: T, save: boolean): (<K extends keyof T>(name: K, value: T[K]) => void) {
		let isNew = this.state() === DtoState.New;
		return (name, value) => {
			if (isNew || (value !== this.sourceData[name] && value != null)) {
				result[name] = value;
			}
			if (save) {
				this.sourceData[name] = value;
			}
		};
	}

	protected getMappedArrayFromDataSource(array: any[], mapFn: (item: any) => any): any[] {
		let newArray: any[] = [];
		for (let item of array || []) {
			newArray.push(mapFn(item));
		}
		return newArray;
	}

	protected mapArrayChanges(result: any, arrayToMap: any, key: string) {
		let changedItems: any[] = [];
		for (let item of arrayToMap()) {
			if (item.hasChanged()) {
				changedItems.push(item.changedProperties(false));
			}
		}

		if (changedItems.length > 0) {
			result[key] = changedItems;
		}
	}

	protected initializeBase(data: T) {
		if (data) {
			this.id(data.id);
			this.rowversion(data.rowversion);
			this.state(data.state);
			this.sourceData = data;
		} else {
			this.state(DtoState.New);
			this.sourceData = { state: DtoState.New } as T;
		}
	}

	protected setChangedBaseProperties(result: T) {
		result.id = this.id();
		result.rowversion = this.rowversion();
		result.state = this.state();
		return result;
	}

	protected async saveAsyncBase(url: string, errorTitle: string = "Fehler beim Speichern", refreshAfterSave: boolean = true): Promise<boolean> {
		// hack to wait for dataGrid datasource changes from editing cells (23604)
		await new Promise(resolve => setTimeout(resolve, 0));

		if (!this.hasChanged()) {
			Logger.warn("No changes detected!");
			return true;
		}
		let saveParams = this.changedProperties(false);
		let args: IDialogRequestOptions = { url, params: saveParams, method: "POST", showInfo: true, returnCheckResultData: true };

		let result: CheckResultData = await DialogResultVm.getDataWithWarningDialogRetryAsync(args);

		if (!result || !result.success || !result.resultData) {
			return false;
		}

		if (NumberHelper.isNumberZeroOrGreater(result.resultData.id)) {
			let newId = result.resultData.id;
			if (newId !== this.id()) {
				this.sourceData.id = newId;
				this.id(newId);
			}
		}

		if (refreshAfterSave) {
			this.changedProperties(true);
		}

		this.showSavedMessage();
		return true;
	}

	protected async newAsyncBase(url: string, params: AjaxParams, errorTitle: string = "Fehler beim Anlegen eines neuen Elements"): Promise<T> {
		let result: T = await DialogResultVm.getDataWithWarningDialogRetryAsync({
			url,
			params,
			title: errorTitle,
			errorMessage: errorTitle,
			method: "GET"
		}).catch(ErrorHandler.log());

		return result;
	}

	protected async newAsyncGenricBase<TDto>(url: string, params: AjaxParams, errorTitle: string): Promise<TDto> {
		let result: any = await this.newAsyncBase(url, params, errorTitle);
		return result;
	}

	protected showNotSavedError(message = "Die Änderungen konnten nicht gespeichert werden.") {
		MessageHelper.uiError(message);
	}

	protected showSavedMessage(message = "Die Änderungen wurden gespeichert.") {
		MessageHelper.uiSuccess(message);
	}

	protected initDirect(name: Extract<keyof T, string>) {
		let src: any = this.sourceData;
		let srcValue = src[name + "Specified"] !== false ? src[name] : null;
		(this as any)[name](srcValue);
	}

	protected initModel(name: Extract<keyof T, string>, ctor: any) { // TODO(jpg): type
		let src: any = this.sourceData;
		let srcValue = src[name + "Specified"] !== false ? new ctor(src[name]) : null;
		(this as any)[name](srcValue);
	}

	protected initModelArray(name: Extract<keyof T, string>, ctor: any) { // TODO(jpg): type
		let src: any = this.sourceData;
		let srcValue = src[name + "Specified"] !== false ? src[name] || [] : [];

		let items: any[] = [];
		for (let item of srcValue) {
			items.push(new ctor(item));
		}
		(this as any)[name](items);
	}

	protected mapDirect(required: boolean, result: T, name: Extract<keyof T, string>, save: boolean) {
		let srcItem = (this.sourceData as any)[name];
		let item = (this as any)[name]();
		if (required || (item == null ? srcItem != null : item !== srcItem)) {
			(result as any)[name] = item;
			(result as any)[name + "Specified"] = true;
		}
		if (save) {
			(this.sourceData as any)[name] = item;
		}
	}

	protected mapModel(result: T, name: Extract<keyof T, string>, save: boolean) {
		let item = (this as any)[name]();
		if (item != null && (item.hasChanged == null || item.hasChanged())) {
			(result as any)[name] = item.changedProperties(save);
			(result as any)[name + "Specified"] = true;
		}
	}

	protected mapModelArray(result: T, name: Extract<keyof T, string>, save: boolean) {
		(result as any)[name] = [];
		for (let item of (this as any)[name]() || []) {
			if (item != null && (item.hasChanged == null || item.hasChanged())) {
				(result as any)[name].push(item.changedProperties(save));
				(result as any)[name + "Specified"] = true;
			}
		}
	}

	private setChangedKey(key: string) {
		if (this.sourceData == null) {
			return;
		}

		if (this.changedKeys.indexOf(key) === -1) {
			this.changedKeys.push(key);
		}
	}
}

export abstract class OwningModel<T extends OwningDto> extends BaseModel<T> {
	public ownerMktId: KnockoutObservable<number> = ko.observable(null);
	public readMktIdSet: KnockoutObservable<number> = ko.observable(null);
	public writeMktIdSet: KnockoutObservable<number> = ko.observable(null);

	public readMktIdSetAsArray: KnockoutComputed<number[]> = ko.pureComputed({
		read: () => {
			return BitConverter.toBitField(this.readMktIdSet());
		},
		write: (newValue) => {
			// write owner mkt always to read mkt id set
			if (newValue.indexOf(this.ownerMktId()) === -1) {
				newValue.push(this.ownerMktId());
			}

			// write all mkts in write mkt set (since if you can change you should also read)
			let inWrite = ArrayHelper.diff(this.writeMktIdSetAsArray(), newValue);
			for (let item of inWrite) {
				newValue.push(item);
			}

			this.readMktIdSet(BitConverter.fromBitField(newValue));
		}
	});

	public writeMktIdSetAsArray: KnockoutComputed<number[]> = ko.pureComputed({
		read: () => {
			return BitConverter.toBitField(this.writeMktIdSet());
		},
		write: (newValue) => {
			// write owner mkt always to write mkt id set
			if (newValue.indexOf(this.ownerMktId()) === -1) {
				newValue.push(this.ownerMktId());
			}
			let readMkIdSet = this.readMktIdSetAsArray();
			let added = ArrayHelper.diff(newValue, readMkIdSet);
			this.writeMktIdSet(BitConverter.fromBitField(newValue));
			// change read mktIdSet as same
			this.readMktIdSet(Bit.or(this.readMktIdSet(), BitConverter.fromBitField(added)));
		}
	});

	public initializeOwning(data: T) {
		this.initializeBase(data);
		if (data) {
			this.ownerMktId(data.ownerMktId);
			this.readMktIdSet(data.readMktIdSet);
			this.writeMktIdSet(data.writeMktIdSet);
		}
	}

	public setChangedOwningProperties(result: T, save: boolean) {
		let map = this.getPropertyMapper(result, save);
		map("ownerMktId", this.ownerMktId());
		map("readMktIdSet", this.readMktIdSet());
		map("writeMktIdSet", this.writeMktIdSet());

		return this.setChangedBaseProperties(result);
	}
}